import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { saveRequest, ApiError, emailRequest, pdfRequest } from "./endpoints";
import type {
	EmailRequest,
	EmailResponse,
	PdfRequest,
	PdfResponse,
	SaveRequest,
	SaveResponse,
} from "./schemas";
import { ApiDataContext } from "src/api/api-data-context";
import { useUiData } from "src/ui";
import { Term } from "@definitions";

const saveSuccessTerm: Term = "request:save-success";
const saveNameTerm: Term = "ui-buttons:save";
const emailNameTerm: Term = "ui-buttons:email";

export function useSave() {
	const { localizer } = useUiData();
	const {
		guid,
		pricelistId,
		addApiNotification,
		exportData,
		getSerializedConfiguration,
		generateSnapshot,
		setName,
		setDesignId,
		setIsLoading,
	} = useContext(ApiDataContext);
	const { mutate: performSaveRequest } = useMutation({
		mutationFn: (body: SaveRequest) => saveRequest(guid, pricelistId, body),
		onMutate: () => {
			setIsLoading(true);
		},
		onError: (err: ApiError) =>
			addApiNotification({
				status: "error",
				name: err.name,
				message: err.message,
				requestType: "save",
			}),
		onSuccess: (res) => {
			const typedResponse = res as SaveResponse;
			const { slug, name } = typedResponse.data;
			setDesignId(slug);
			setName(name);
			addApiNotification({
				status: "success",
				name: localizer.localizeTerm(saveNameTerm),
				message: localizer.localizeTerm(saveSuccessTerm),
				requestType: "save",
			});
		},
		onSettled: () => {
			setIsLoading(false);
		},
	});

	const saveConfiguration = async (name?: string) => {
		const configuration = getSerializedConfiguration();
		const imageSrc = await generateSnapshot();
		const payload: SaveRequest = {
			articles: exportData.items,
			snapshot: imageSrc,
			configuration,
			name: name || exportData.name,
		};
		await performSaveRequest(payload);
	};
	return saveConfiguration;
}

export function useEmail() {
	const { localizer } = useUiData();
	const {
		guid,
		pricelistId,
		addApiNotification,
		exportData,
		getSerializedConfiguration,
		generateSnapshot,
		setIsLoading,
	} = useContext(ApiDataContext);
	const { mutateAsync: performEmailRequest } = useMutation({
		mutationFn: (body: EmailRequest) => emailRequest(guid, pricelistId, body),
		onMutate: () => {
			setIsLoading(true);
		},
		onError: (err: ApiError) =>
			addApiNotification({
				status: "error",
				name: err.name,
				message: err.message,
				requestType: "email",
			}),
		onSuccess: (res) => {
			const typedResponse = res as EmailResponse;
			addApiNotification({
				status: "success",
				name: localizer.localizeTerm(emailNameTerm),
				message: typedResponse.data.message,
				requestType: "email",
			});
		},
		onSettled: () => {
			setIsLoading(false);
		},
	});

	const sendEmail = async (email: string, message: string) => {
		const configuration = getSerializedConfiguration();
		const imageSrc = await generateSnapshot();
		const payload: EmailRequest = {
			articles: exportData.items,
			snapshot: imageSrc,
			configuration,
			name: exportData.name,
			email,
			message,
		};
		try {
			await performEmailRequest(payload);
		} catch {
			// Already reported through the mutation's onError notification.
		}
	};
	return sendEmail;
}

export function useSaveAndPdf() {
	const { localizer } = useUiData();
	const {
		guid,
		pricelistId,
		addApiNotification,
		exportData,
		getSerializedConfiguration,
		generateSnapshot,
		setName,
		setDesignId,
		setIsLoading,
		countryCode,
	} = useContext(ApiDataContext);
	// Opening the generated PDF is the success signal, so only errors notify.
	const { mutateAsync: performPdfRequest } = useMutation({
		mutationFn: (body: PdfRequest) =>
			pdfRequest(guid, pricelistId, localizer.locale, countryCode, body),
		onMutate: () => {
			setIsLoading(true);
		},
		onError: (err: ApiError) =>
			addApiNotification({
				status: "error",
				name: err.name,
				message: err.message,
				requestType: "pdf",
			}),
		onSuccess: (res) => {
			const typedResponse = res as PdfResponse;
			const { design, pdf_url: pdfUrl } = typedResponse.data;
			setDesignId(design.slug);
			setName(design.name);
			window.open(pdfUrl, "_blank", "noopener,noreferrer");
		},
		onSettled: () => {
			setIsLoading(false);
		},
	});

	const saveAndGetPdf = async (name?: string) => {
		const configuration = getSerializedConfiguration();
		const imageSrc = await generateSnapshot();
		const payload: PdfRequest = {
			articles: exportData.items,
			snapshot: imageSrc,
			configuration,
			name: name || exportData.name,
		};
		try {
			await performPdfRequest(payload);
		} catch {
			// Already reported through the mutation's onError notification.
		}
	};
	return saveAndGetPdf;
}
